import {
  listRow,
  getExtraBurgerMenuOptions,
  MenuOption,
} from '@standard-angular-utilities/shared-components';
import { UserService } from 'BaseServices/user-service';
import { MatDialog } from '@angular/material/dialog';
import { Component, inject, ViewChild } from '@angular/core';
import { Customer, Project } from 'TimelogDataTypes';

import { ProjectService } from 'TimelogServices/project-service';
import { CustomerService } from 'TimelogServices/customer-service';
import { MatButtonModule } from '@angular/material/button';
import { ProjectDialogComponent } from '../../Dialogs/project-dialog/project-dialog.component';
import { forkJoin } from 'rxjs';
import { SharedFunctionsService } from 'BaseServices/sharedFunctions.service';
import { LogDTO, LogType, User } from 'BaseDataTypes';
import { LogService } from 'BaseServices/log-service';
import { LogHelperService } from 'BaseServices/log-helper.service';
import {
  BaseListPageComponent,
  multiSelectFilterList,
} from 'src/app/BasePages/base-list-page/base-list-page.component';

@Component({
  selector: 'app-project-view',
  imports: [MatButtonModule, BaseListPageComponent],
  templateUrl: './project-view.component.html',
  styleUrls: [
    '../../../../../../src/app/general-styles/pages.css',
    './project-view.component.css',
  ],
})
export class ProjectViewComponent {
  @ViewChild('baseListPage')
  baseListPage!: BaseListPageComponent<Project>;

  readonly dialog = inject(MatDialog);
  logHeaderList: string[];
  customHeaderList: string[] = [
    'Kunde',
    'Projektnavn',
    'Projektnummer',
    'Projektleder',
    'Lukket dato',
  ];
  headerList: string[] = this.customHeaderList;
  isHistory: boolean = false;
  projects: Project[] = [];
  customers: Customer[] = [];
  projectNumbers: { id: number; name: string; value: number }[] = [];
  selectedCustomers: number[] = [];
  selectedUsers: number[] = [];
  selectedProjectNumbers: number[] = [];
  users: User[] = [];
  transformedUsers: User[] = [];

  multiSelectFilterLists: multiSelectFilterList[] = [];

  constructor(
    private projectService: ProjectService,
    private customerService: CustomerService,
    private userService: UserService,
    private sharedFunctionsService: SharedFunctionsService,
    private logService: LogService,
    private logHelper: LogHelperService
  ) {
    this.logHeaderList = this.logService.getLogHeaders('Projekt');
  }

  /**
   * Retrives customer and user data and updates project list
   */
  ngOnInit() {
    forkJoin({
      projects: this.projectService.getProjects(),
      customers: this.customerService.getCustomers(),
      users: this.userService.getUsers(),
    }).subscribe(({ projects, customers, users }) => {
      // Process customers

      this.customers = customers.filter((customer) =>
        this.sharedFunctionsService
          .getDistinctIds(projects, (x) => x.customer.id)
          .includes(customer.id)
      );
      this.selectedCustomers = this.customers.map((c) => c.id);

      // process users
      this.users = users.filter((user) =>
        this.sharedFunctionsService
          .getDistinctIds(projects, (x) => x.projectManagerId)
          .includes(user.id)
      );
      this.users.unshift({ id: 0, name: '' } as User);
      this.selectedUsers = this.users.map((c) => c.id);

      this.selectedProjectNumbers = projects.map((p) =>
        p.latisNum != null ? p.latisNum : 0
      );

      this.projectNumbers = projects.map((project) => ({
        id: project.id,
        name: project.latisNum ? String(project.latisNum) : '',
        value: project.latisNum ? project.latisNum : 0,
      }));

      this.transformedUsers = this.sharedFunctionsService.transformItems(
        this.users,
        (user) => this.sharedFunctionsService.transformUserName(user)
      );

      this.updateMultiSelectFilterLists();
      this.updateListProjects();
    });
  }

  /**
   * Opens a dialog with project details
   * If project is null, a dialog opens with default details, for creating a new project
   * @param project
   */
  projectClicked = (project?: Project) => {
    if (project == null) {
      project = {
        id: 0,
        name: '',
        customerId: 0,
        projectManagerId: 0,
        closedDate: null,
      } as Project;
    }
    let dialogref = this.dialog.open(ProjectDialogComponent, {
      data: { data: [project] },
    });
    dialogref.afterClosed().subscribe((result) => {
      if (result == true) {
        this.updateListProjects();
      }
    });
  }

  /**
   * Builds the multiSelectFilterLists used by BaseListPageComponent.
   */
  updateMultiSelectFilterLists() {
    this.multiSelectFilterLists = [
      {
        Items: this.customers,
        selectedItemsIds: this.selectedCustomers,
        placeHolderText: 'Søg kunde',
        selectedValueChange: (event: number[]) => {
          this.selectedCustomers = event;
        },
        filterFunction: (project: Project) =>
          this.selectedCustomers.includes(project.customerId),
        HideWithHeaderIndex: 0,
      },
      {
        Items: this.projectNumbers,
        selectedItemsIds: this.selectedProjectNumbers,
        placeHolderText: 'Søg projektnummer',
        selectedValueChange: (event: number[]) => {
          this.selectedProjectNumbers = event;
        },
        filterFunction: (project: Project) =>
          project.latisNum == null || this.selectedProjectNumbers.includes(project.id),
        HideWithHeaderIndex: 2,
      },
      {
        Items: this.transformedUsers,
        selectedItemsIds: this.selectedUsers,
        placeHolderText: 'Søg projektleder',
        selectedValueChange: (event: number[]) => {
          this.selectedUsers = event;
        },
        filterFunction: (project: Project) =>
          project.projectManagerId == null ||
          this.selectedUsers.includes(project.projectManagerId),
        HideWithHeaderIndex: 3,
      },
    ];
  }

  /**
   * Function which switches the list between projects and the event log.
   */
  switchDataInList = (isHistory: boolean) => {
    if (isHistory) {
      this.updateListProjects();
    } else {
      this.updateListWithLogData();
    }
  };

  /**
   * Function to update list with eventlog data
   */
  updateListWithLogData() {
    this.logHelper.updateLogList(
      LogType.Project,
      this.getProjectNameFromId.bind(this),
      (data) => this.baseListPage.UpdateLogListData(data)
    );
  }

  getProjectNameFromId(id: number): string {
    return this.projects.find((p) => p.id === id)?.name || id.toString();
  }

  /**
   * function to update list with project data
   */
  updateListProjects() {
    this.projectService.getProjects().subscribe((result) => {
      let tempList: listRow<Project>[] = [];
      //iteration through the projects
      result.forEach((project) => {
        let projectManagerName = '';

        // finding the project manager's name
        if (project.projectManagerId != null) {
          let projectManager = this.users.find(
            (user) => user.id == project.projectManagerId
          );
          if (projectManager != null) {
            projectManagerName = projectManager.name;
          }
        }
        tempList.push({
          object: project,
          id: project.id,
          data: [
            project.customer?.name ?? '',
            project.name,
            project.latisNum!.toString() ?? '',
            projectManagerName,
            project.closedDate
              ? new Date(project.closedDate).toLocaleDateString()
              : '',
          ],
        });

        const exists = this.projectNumbers.some(
          (item) => item.name === String(project.latisNum)
        );
        if (!exists) {
          this.projectNumbers.push({
            id: project.id,
            name: String(project.latisNum),
            value: project.latisNum ? project.latisNum : 0,
          });
          this.selectedProjectNumbers.push(project.id);
        }
      });
      this.projects = result;
      this.baseListPage.UpdateListData(tempList);
    });
  }
}
